import React, { useEffect, useState } from 'react';
import { PDFViewer, PDFDownloadLink } from '@react-pdf/renderer';
import { Button } from 'antd';
import { useMediaQuery } from 'react-responsive';
import { getAccessTokenApi } from 'api/auth';
import { getStaticMantencionData } from 'api/budget';
import useSignatureUrls from 'hooks/useSignatureUrls';
import TemplateRenderer from 'PDF/TemplateEngine/TemplateRenderer';
import { useTenantColors } from 'hooks/useTenantColors';
import useTemplateOrFallback from 'PDF/TemplateEngine/useTemplateOrFallback';
import { mapInformeData, withTenantTexts } from './index';

// Reemplazo lowcode del visor de Informe de Mantención. Mantiene la firma de
// entrada de MantencionDownload: recibe `mantencion` con { Ascensores,
// mantencion, actividades }.
//
// NOTA sobre firmas: SignaturesSection lee los datos de firmas desde `data`,
// NO desde `config`. Las claves que consume son:
//   data.specialist_name, data.specialist_signature
//   data.recepcionist_name, data.recepcionist_signature
// La visibilidad (showSpecialist / showReceptionist) se configura en
// section.config del template (ya declarado en template.js).
// Por eso las URLs resueltas y los nombres se inyectan en `data` (vía
// withSignatures), no en `docConfig`.
function withSignatures(data, signatureUrls, signaturesReady, mantencionData) {
  if (!signaturesReady) return data;
  return {
    ...data,
    specialist_name: mantencionData?.Responsable?.fullname || null,
    specialist_signature: signatureUrls.firma_spec || null,
    recepcionist_name: mantencionData?.name_recep || null,
    recepcionist_charge: mantencionData?.cargo_recep || null,
    recepcionist_signature: signatureUrls.firma_recep || null,
  };
}

function InformeMantencionDownload({ mantencion }) {
  const { mantencion: mantencionData, actividades } = mantencion;
  const tenantColors = useTenantColors();
  const isMobile = useMediaQuery({ maxWidth: 767 });

  const { urls: signatureUrls, ready: signaturesReady } = useSignatureUrls({
    firma_spec: mantencionData?.firma_spec,
    firma_recep: mantencionData?.firma_recep,
  });

  const [staticData, setStaticData] = useState(null);
  const [config, setConfig] = useState({});
  const token = getAccessTokenApi();
  // Bandera de carga: `staticData` arranca en null y el documento se arma igual,
  // asi que sin esto el boton queda disponible con los datos del tenant todavia
  // en vuelo y el PDF sale sin el header.
  const [staticReady, setStaticReady] = useState(false);
  useEffect(() => {
    let cancelled = false;
    setStaticReady(false);
    getStaticMantencionData(token)
      .then((result) => {
        if (cancelled) return;
        setStaticData(result.static);
        setConfig(result.tenant);
      })
      // `finally` y no `then`: si el fetch falla, el boton se habilita igual en
      // vez de quedar colgado para siempre.
      .catch((err) => {
        // Sin este catch la cadena termina en un unhandled rejection cuando el
        // fetch falla. El documento se arma sin este dato, pero el boton se
        // habilita igual (ver el `finally`) para no dejarlo colgado.
        console.error('[PDF] No se pudo cargar un dato del documento:', err);
      })
      .finally(() => {
        if (!cancelled) setStaticReady(true);
      });
    return () => {
      cancelled = true;
    };
  }, [token]);

  const { template, loading: templateLoading } = useTemplateOrFallback('informe_mantencion');

  const baseData = mapInformeData({
    mantencion: mantencionData,
    Cliente: mantencionData?.Cliente,
    Responsable: mantencionData?.Responsable,
    actividades,
  });
  const dataWithTexts = withTenantTexts(baseData, staticData);
  // Las firmas van en `data` porque SignaturesSection las lee de data,
  // no de config (ver SignaturesSection.jsx líneas 15-22).
  const data = withSignatures(dataWithTexts, signatureUrls, signaturesReady, mantencionData);

  // docConfig solo controla logo y metadatos del tenant; las firmas
  // NO van aquí (van en data, ver withSignatures arriba).
  const docConfig = {
    ...config,
  };

  if (templateLoading || !template) {
    return <div>Cargando plantilla...</div>;
  }

  // El `loading` de PDFDownloadLink solo dice si termino de ARMAR el PDF, no si
  // los datos llegaron. Sin esta guarda, un clic temprano baja el informe sin
  // los datos del tenant en el header o sin las firmas resueltas.
  if (!staticReady || !signaturesReady) {
    return <div>Cargando datos...</div>;
  }

  const doc = (
    <TemplateRenderer template={template} data={data} config={docConfig} colors={tenantColors} />
  );

  return (
    <div>
      {!isMobile && <PDFViewer style={{ width: '100%', height: '80vh' }}>{doc}</PDFViewer>}
      <div style={{ marginTop: 20, textAlign: 'center' }}>
        <PDFDownloadLink document={doc} fileName={`Informe de mantención N°${mantencionData?.id}`}>
          {({ loading }) => (
            <Button type="primary" disabled={loading} block>
              {loading ? 'Generando documento...' : 'Descargar PDF'}
            </Button>
          )}
        </PDFDownloadLink>
      </div>
    </div>
  );
}

export default InformeMantencionDownload;
